import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { ArrowLeft, Inbox, ShoppingBag, User, ClipboardList, Smartphone } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { placed: "Placed", in_progress: "Preparing", delivered: "Delivered", cancelled: "Cancelled" };
const STATUS_TONE = {
  placed: "bg-indigo-100 text-indigo-700",
  in_progress: "bg-amber-100 text-amber-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const NEXT_ACTIONS = [
  { v: "in_progress", label: "Accept / Preparing", tone: "bg-amber-500 hover:bg-amber-600" },
  { v: "delivered", label: "Mark Delivered", tone: "bg-emerald-600 hover:bg-emerald-700" },
  { v: "cancelled", label: "Reject", tone: "bg-red-600 hover:bg-red-700" },
];

export default function InboundOrders() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = () => {
    setLoading(true);
    apiClient.get("/inbound-orders")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await apiClient.put(`/inbound-orders/${id}/status`, { status });
      toast.success(`Marked ${STATUS_LABEL[status] || status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <AppHeader title="Incoming Orders" subtitle={`${items.length} from linked buyers`} />
      <div className="px-4 pt-4 pb-28 space-y-3">
        <button onClick={() => navigate("/orders")} data-testid="inbound-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back to my orders
        </button>

        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200">
            <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto grid place-items-center mb-3">
              <Inbox className="w-7 h-7 text-slate-400" strokeWidth={2.5} />
            </div>
            <p className="text-slate-700 font-semibold mb-1">No incoming orders yet</p>
            <p className="text-sm text-slate-500">
              Share your supplier code from the Suppliers page so buyers can send orders directly.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="inbound-orders-list">
            {items.map((o) => (
              <div key={o.id} data-testid={`inbound-card-${o.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate flex items-center gap-1.5">
                      <ShoppingBag className="w-4 h-4 text-emerald-700 shrink-0" />
                      {o.product}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>
                        {o.lines && o.lines.length > 1
                          ? `${o.lines.length} line items`
                          : `Qty: ${o.quantity} ${o.unit}`}
                      </span>
                      <span data-testid={`inapp-badge-${o.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full">
                        <Smartphone className="w-3 h-3" /> Placed via app
                      </span>
                    </div>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_TONE[o.status]}`}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </div>

                {o.lines && o.lines.length > 1 && (
                  <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-200">
                    {o.lines.map((ln, i) => (
                      <div key={i} className="flex items-center justify-between p-2 text-xs">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-800 truncate">{ln.product}</div>
                          <div className="text-slate-500">Qty: {ln.quantity} {ln.unit}</div>
                        </div>
                        <div className="font-bold text-slate-900 shrink-0">₹{ln.price.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5 text-slate-700 min-w-0">
                    <User className="w-4 h-4 text-indigo-600 shrink-0" />
                    <span className="truncate">{o.buyer_name || "Buyer"}</span>
                  </div>
                  <div className="font-bold text-emerald-700 shrink-0">₹{o.price.toFixed(2)}</div>
                </div>

                {o.status !== "delivered" && o.status !== "cancelled" && (
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {NEXT_ACTIONS.map((a) => (
                      <Button
                        key={a.v}
                        disabled={busyId === o.id || o.status === a.v}
                        onClick={() => setStatus(o.id, a.v)}
                        data-testid={`inbound-status-${a.v}-${o.id}`}
                        className={`h-10 rounded-lg text-white text-xs font-semibold ${a.tone} ${o.status === a.v ? "opacity-60" : ""}`}
                      >
                        {a.label}
                      </Button>
                    ))}
                  </div>
                )}

                <Button
                  onClick={() => navigate(`/tasks/new?order_id=${o.id}&title=${encodeURIComponent(`Fulfill inbound: ${o.product}`)}`)}
                  data-testid={`inbound-assign-btn-${o.id}`}
                  variant="outline"
                  className="w-full h-11 rounded-lg border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-semibold gap-2"
                >
                  <ClipboardList className="w-4 h-4" /> Assign to employee
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
